import { BaseQueryFn, FetchArgs, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { QueryActionCreatorResult } from '@reduxjs/toolkit/dist/query/core/buildInitiate';
import { createContext, FC, ReactNode, useContext, useState } from 'react';

import { useGetProfileDataQuery } from 'services/talento';
import { selectUserInfo } from 'store';
import { useAppSelector } from 'store/hooks';
import { ICustomError, IProfile, IProfileForm, IResponseProfile } from 'utils/interfaces';

interface ProfileFormContextType {
  email: string;
  currentTab: number;
  setCurrentTab: (tab: number) => void;
  formValues: IProfileForm;
  setFormValues: (value: (previousState: IProfileForm) => IProfileForm) => void;
  isEditing: boolean;
  setIsEditing: (state: boolean) => void;
  profileData: IProfile;
  isLoadingProfile: boolean | undefined;
  refetchProfile: () => QueryActionCreatorResult<
    QueryDefinition<
      { email: string },
      BaseQueryFn<string | FetchArgs, unknown, ICustomError, {}>,
      never,
      IProfile,
      'talentoApi'
    >
  >;
  isFetchingProfile: boolean | undefined;
  isErrorProfileSubmiting: boolean | undefined;
}

interface ProfileFormContextProviderProps {
  children: ReactNode;
}

const INITIAL_DATA: IProfileForm = {
  email: '',
  names: '',
  secondName: '',
  lastname: '',
  secondLastName: '',
  preferredName: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  phoneCode: 0,
  direction: '',
  bloodType: '',
  idType: '',
  numberId: '',
  bornDate: '',
  institution: '',
  title: '',
  jobtitle: '',
  company: '',
  job_start_date: '',
  job_end_date: '',
  currentSalaryCurrency: '',
  currentSalary: '',
  expectedSalaryCurrencyDropdown: '',
  expectedSalaryNumeric: '',
  hasExperience: false,
};

const ProfileFormContext = createContext<ProfileFormContextType>({} as ProfileFormContextType);

export const ProfileFormContextProvider: FC<ProfileFormContextProviderProps> = ({ children }) => {
  const { email } = useAppSelector(selectUserInfo);

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [formValues, setFormValues] = useState<IProfileForm>(INITIAL_DATA);
  const [isEditing, setIsEditing] = useState(true);

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    refetch: refetchProfile,
    isFetching: isFetchingProfile,
    error: isErrorProfileSubmiting,
  } = useGetProfileDataQuery<IResponseProfile>({ email }, { refetchOnMountOrArgChange: true });

  const value = {
    email,
    currentTab,
    setCurrentTab,
    formValues,
    setFormValues,
    isEditing,
    setIsEditing,
    profileData,
    isLoadingProfile,
    refetchProfile,
    isFetchingProfile,
    isErrorProfileSubmiting,
  };

  return <ProfileFormContext.Provider value={value}>{children}</ProfileFormContext.Provider>;
};

export const useProfileFormContext = (): ProfileFormContextType => {
  const context = useContext(ProfileFormContext);

  if (!context) {
    throw new Error('useProfileFormContext debe usarse dentro de un ProfileFormContextProvider');
  }
  return context;
};
